import React, { useState, useEffect } from 'react';
import { getProperties } from '../../../services/mysteryShopService';
import type { MysteryShopProperty } from '../../../types/mysteryShop';
import { getScoreColor } from '../utils';

interface AllPropertiesProps {
  onSelectProperty: (id: string) => void;
}

export const AllProperties: React.FC<AllPropertiesProps> = ({ onSelectProperty }) => {
  const [properties, setProperties] = useState<MysteryShopProperty[]>([]);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    getProperties().then(setProperties);
  }, [rev]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Properties</h1>
          <p className="text-gray-500 mt-1">{properties.length} properties in portfolio</p>
        </div>
        <button
          onClick={() => setRev(r => r + 1)}
          className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          title="Refresh"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2v6h-6"/>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
            <path d="M3 22v-6h6"/>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
          </svg>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="px-5 py-4">Property</th>
                <th className="px-5 py-4">Address</th>
                <th className="px-5 py-4 text-center">Calls</th>
                <th className="px-5 py-4 text-center">Avg Score</th>
                <th className="px-5 py-4 text-center">Tour Rate</th>
                <th className="px-5 py-4 text-center">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {properties.map(prop => (
                <tr
                  key={prop.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => onSelectProperty(prop.id)}
                >
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-800">{prop.name}</p>
                  </td>
                  <td className="px-5 py-4 text-gray-600 text-sm">{prop.address}</td>
                  <td className="px-5 py-4 text-center text-gray-600">{prop.performance?.callCount ?? 0}</td>
                  <td className="px-5 py-4 text-center">
                    {prop.performance?.avgScore != null ? (
                      <span className="font-semibold" style={{ color: getScoreColor(prop.performance.avgScore) }}>
                        {prop.performance.avgScore.toFixed(0)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-center text-gray-600">
                    {prop.performance?.tourRate != null ? `${prop.performance.tourRate.toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-5 py-4 text-center">
                    {prop.performance?.trend != null ? (
                      <span className={prop.performance.trend >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {prop.performance.trend >= 0 ? '↑' : '↓'} {Math.abs(prop.performance.trend).toFixed(0)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
